import React from 'react';
import { Conflict, Plant } from '../types';
import { plantGlyph } from '../utils/plantGlyph';
import './ConflictToast.css';

type RotationNote = { plant: Plant; previousPlant: Plant; reason: string; status: 'good' | 'bad' };

type Props = {
  conflicts: Conflict[];
  companions: { plant1: Plant; plant2: Plant; reason: string }[];
  rotationNotes: RotationNote[];
  onDismiss: () => void;
};

export default function ConflictToast({ conflicts, companions, rotationNotes, onDismiss }: Props) {
  const rotationWarnings = rotationNotes.filter(r => r.status === 'bad');
  const rotationGood = rotationNotes.filter(r => r.status === 'good');
  const hasContent = conflicts.length > 0 || companions.length > 0 || rotationNotes.length > 0;
  const hasWarnings = conflicts.length > 0 || rotationWarnings.length > 0;

  if (!hasContent) return null;

  const hasMixing = conflicts.length > 0 || companions.length > 0;
  const mixingIsWarning = conflicts.length > 0;
  const mixingTitle = mixingIsWarning
    ? (companions.length > 0 ? 'Mixed planting result' : 'Planting conflict!')
    : 'Great combination!';

  return (
    <div className={`toast ${hasWarnings ? 'toast--warning' : 'toast--success'}`} role="alert">
      <button className="toast__close" onClick={onDismiss} aria-label="Dismiss">×</button>

      {hasMixing && (
        <section className="toast__section">
          <div className="toast__header">
            <span className="toast__icon">{mixingIsWarning ? '⚠️' : '🌟'}</span>
            <strong className="toast__title">{mixingTitle}</strong>
          </div>
          <ul className="toast__list">
            {companions.map((c, i) => (
              <li key={`comp-${i}`} className="toast__item">
                <span className="toast__sr-only">Good pairing: </span>
                <span>{plantGlyph(c.plant1)} {c.plant1.name}</span>
                <span className="toast__amp">&amp;</span>
                <span>{plantGlyph(c.plant2)} {c.plant2.name}</span>
                <span className="toast__reason">{c.reason || 'these grow really well together!'}</span>
              </li>
            ))}
            {conflicts.map((c, i) => (
              <li key={`conf-${i}`} className="toast__item toast__item--bad">
                <span className="toast__sr-only">Warning: </span>
                <span>{plantGlyph(c.plant1)} {c.plant1.name}</span>
                <span className="toast__amp">&amp;</span>
                <span>{plantGlyph(c.plant2)} {c.plant2.name}</span>
                <span className="toast__reason">{c.reason}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {rotationGood.length > 0 && (
        <section className="toast__section">
          <div className="toast__header">
            <span className="toast__icon">🔄</span>
            <strong className="toast__title">Good rotation</strong>
          </div>
          <ul className="toast__list">
            {rotationGood.map((r, i) => (
              <li key={`rotgood-${i}`} className="toast__item toast__item--rotation">
                <span className="toast__reason toast__reason--rotation">{r.reason}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {rotationWarnings.length > 0 && (
        <section className="toast__section">
          <div className="toast__header">
            <span className="toast__icon">⚠️</span>
            <strong className="toast__title">Rotation warning</strong>
          </div>
          <ul className="toast__list">
            {rotationWarnings.map((r, i) => (
              <li key={`rot-${i}`} className="toast__item toast__item--rotation">
                <span className="toast__sr-only">Warning: </span>
                <span className="toast__reason toast__reason--rotation">{r.reason}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
